import React, { FC, memo, useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Ellipsis, LibTooltipPosition } from '@infologistics/frontend-libraries';
import SystemHint from '@common/SystemHint';
import { useAppSelector } from '@hooks/redux';
import { IEnvelopeField } from '@store/modules/boxes/types';
import { IEnvelope } from '@store/modules/envelopes/types';

import { ENVELOPE_LIST_PREVIEW_LIMIT } from '../../envelopeViewSettings';
import { getEnvelopeFieldDisplayValue } from '../../envelopeFieldDisplay';
import styles from './EnvelopeItem.module.scss';

interface IProps {
  envelopeFields: IEnvelopeField[];
  fields: IEnvelope['fields'];
  knownFieldKeys: string[];
}

interface IFieldDisplayItem {
  key: string;
  label: string;
  value: string;
  isUnknown?: boolean;
}

const EnvelopeFieldsListCell: FC<IProps> = ({ envelopeFields, fields, knownFieldKeys }) => {
  const { t } = useTranslation(['common', 'boxes']);
  const locale = useAppSelector((state) => state.user.profile.locale);

  const knownFieldKeySet = useMemo(() => new Set(knownFieldKeys), [knownFieldKeys]);

  const knownItems = useMemo(
    () => envelopeFields
      .filter((field) => Object.prototype.hasOwnProperty.call(fields ?? {}, field.systemKey))
      .map((field): IFieldDisplayItem => ({
        key: field.systemKey,
        label: field.name || field.systemKey,
        value: getEnvelopeFieldDisplayValue(field, fields?.[field.systemKey], locale, t),
      })),
    [envelopeFields, fields, locale, t]
  );

  const unknownItems = useMemo(
    () => Object.entries(fields ?? {})
      .filter(([key]) => !knownFieldKeySet.has(key))
      .map(([key, value]): IFieldDisplayItem => ({
        key,
        label: key,
        value: getEnvelopeFieldDisplayValue(null, value, locale, t),
        isUnknown: true,
      })),
    [fields, knownFieldKeySet, locale, t]
  );

  const items = useMemo(
    () => [...knownItems, ...unknownItems],
    [knownItems, unknownItems]
  );
  const visibleItems = useMemo(
    () => items.slice(0, ENVELOPE_LIST_PREVIEW_LIMIT),
    [items]
  );
  const hasPopup = items.length > ENVELOPE_LIST_PREVIEW_LIMIT;

  const renderItem = (item: IFieldDisplayItem): React.ReactElement => (
    <div className={styles.fieldListItem} key={item.key}>
      <Ellipsis externalClass={cn(styles.fieldListName, item.isUnknown && styles.fieldListNameCode)}>
        {item.label}
      </Ellipsis>
      <span className={styles.fieldListSeparator}>:</span>
      <Ellipsis externalClass={styles.fieldListValue}>
        {item.value}
      </Ellipsis>
    </div>
  );

  return (
    <td className={styles.fieldListCell}>
      <SystemHint
        content={hasPopup ? (
          <div className={styles.fieldListTooltip}>
            {items.map(renderItem)}
          </div>
        ) : undefined}
        position={LibTooltipPosition.BOTTOM}
        tooltipClasses={styles.fieldListTooltipContent}
        wrapperClasses={styles.fieldListHint}
      >
        <div className={styles.fieldListPreview}>
          {visibleItems.map(renderItem)}
        </div>
      </SystemHint>
    </td>
  );
};

export default memo(EnvelopeFieldsListCell);
